import type { TFunction } from "i18next";

/** Shape the API helper attaches to a failed request. */
type ApiError = Error & {
  status?: number;
  data?: {
    error?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
};

/**
 * Turns a failed request into a sentence worth showing someone.
 *
 * The server sends a stable code plus an English fallback. We translate the
 * code, feeding `details` in as interpolation values so messages can be
 * specific: naming the game code that is taken, or the limits that were
 * exceeded: rather than restating the rule in developer shorthand.
 */
export function apiErrorMessage(
  e: unknown,
  t: TFunction,
  fallbackKey = "errors.generic"
): string {
  const err = e as ApiError;
  const code = err?.data?.error;
  const details = err?.data?.details ?? {};

  if (code) {
    const key = `errors.${code}`;
    const translated = t(key, {
      ...details,
      defaultValue: "",
    }) as string;
    if (translated) return translated;
  }

  /* No known code: the server's own English, then a generic line. Never show
     a bare "undefined", which is what the old `e?.message` produced when the
     thrown value was a plain object. */
  return err?.data?.message || err?.message || (t(fallbackKey) as string);
}
